import { useNavigate } from "react-router-dom";
import { Button, cn, Divider } from "@nextui-org/react";
import { faCalendar, faClose, faList, faRightFromBracket } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Breakpoint, useBreakpoint } from "cenero-ui-library";
import { useAuthStore } from "@/store/AuthStore";
import { useRouteStore } from "@/store/RouteStore";
import { useSideMenuStore } from "@/store/SideMenuStore";
import { getInitials } from "@/lib/helpers";

// ---------------------- Component

const MenuContents = ({}) => {
  // --------- Variables / Hooks

  const breakpoint = useBreakpoint();

  const navigate = useNavigate();

  const { signOut, userName } = useAuthStore();

  const { currentRoute } = useRouteStore();

  const { isOpen } = useSideMenuStore();

  let isMobile = breakpoint < Breakpoint.md;

  let isExpanded = !isMobile && isOpen;

  // --------- Helpers

  const menuItems = [
    { label: "My Week", icon: faList, path: "/bookings" },
    { label: "Calendar", icon: faCalendar, path: "/calendar" },
    //{ label: "Forms", icon: faBuildingMemo, path: "/forms" },
    { label: "Sign Out", icon: faRightFromBracket, action: signOut },
  ];

  // --------- JSX

  return (
    <>
      <div className="mb-5 flex h-auto w-full flex-row items-start justify-between xs:mb-6 sm:mb-8 md:my-4">
        {/*******************************************/}
        {/* User Section */}
        {/*******************************************/}

        <div
          className={cn(
            "flex h-8 w-full flex-row items-start justify-start xs:h-10",
            isMobile ? "gap-2" : "md:gap-1",
          )}
        >
          <div
            className={cn(
              "flex h-auto items-center transition-all duration-500",
              isMobile ? "hidden" : isExpanded ? "hidden" : "w-8 justify-center",
            )}
          >
            <div className="flex size-7 items-center justify-center rounded-full bg-blue-500 pt-[0.1rem] text-[0.675rem] font-semibold tracking-wider text-white opacity-60 transition-all duration-500">
              {getInitials(userName as string)}
            </div>
          </div>

          <div
            className={cn(
              "flex flex-grow flex-col items-start justify-start overflow-hidden transition-all duration-500",
              isMobile ? "opacity-100" : isExpanded ? "opacity-100" : "w-0 opacity-0",
            )}
          >
            <h2 className="w-full whitespace-nowrap text-base/tight font-bold text-gray-800 dark:text-gray-200 xs:text-lg/tight md:text-[0.675rem] md:font-medium">
              {userName}
            </h2>
            <h3 className="whitespace-nowrap text-xs font-semibold text-gray-400 dark:text-gray-500 xs:text-sm md:text-[0.675rem]/tight">
              Cenero LLC
            </h3>
          </div>
        </div>

        {/*******************************************/}
        {/* Close Button (Mobile Only) */}
        {/*******************************************/}

        {isMobile && (
          <div className="-mr-3 -mt-2 flex h-auto flex-row items-center justify-end sm:-mt-1">
            <Button
              isIconOnly
              radius="full"
              className="bg-transparent p-2 text-current transition-all duration-200"
              onPress={() => navigate(currentRoute as string)}
            >
              <FontAwesomeIcon
                icon={faClose}
                className="h-5 text-gray-800 dark:text-gray-200"
              />
            </Button>
          </div>
        )}
      </div>

      {<Divider />}

      {/*******************************************/}
      {/* Menu Items */}
      {/*******************************************/}

      <div className="mb-7 mt-4 h-auto w-full xs:mt-5 sm:mt-7">
        <div className="flex flex-col gap-4">
          {menuItems.map((item) => (
            <button
              key={item.label}
              onClick={() => (item.action ? item.action() : navigate(item.path))}
              className={cn(
                "flex w-full flex-row items-center",
                isMobile
                  ? "justify-start gap-3"
                  : isExpanded
                    ? "justify-start gap-2"
                    : "justify-center",
              )}
            >
              <div className="mt-1 w-6 md:mt-0">
                <FontAwesomeIcon
                  icon={item.icon}
                  className={cn(
                    "h-6 transition-transform duration-300 md:h-4",
                    !isMobile && !isExpanded && "translate-x-0 transform",
                    currentRoute?.includes(item.path as string)
                      ? "text-blue-500"
                      : "text-gray-700 dark:text-gray-200",
                  )}
                />
              </div>

              <span
                className={cn(
                  "transform overflow-hidden whitespace-nowrap text-base font-semibold transition-all duration-300 xs:text-base md:text-sm",
                  isMobile || isExpanded ? "max-w-32 opacity-100" : "max-w-0 opacity-0",
                  currentRoute?.includes(item.path as string)
                    ? "text-blue-500"
                    : "text-gray-800 dark:text-gray-200",
                )}
              >
                {item.label}
              </span>
            </button>
          ))}
        </div>
      </div>
    </>
  );
};

export default MenuContents;
